import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProfileData, PasswordData, User } from '../../types';
import { API_ENDPOINTS } from '../../config/api.config';
import { apiService } from '../../services/apiService';

export const useProfileManagement = (
    setUser: ((user: User) => void) | undefined,
    setError: (error: string | null) => void,
    setSuccess: (success: string | null) => void,
    setIsLoading: (loading: boolean) => void,
    setShowProfileModal: (show: boolean) => void,
    setShowPasswordModal: (show: boolean) => void,
) => {
    const navigate = useNavigate();
    const [passwordData, setPasswordData] = useState<PasswordData>({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const verifyToken = useCallback((): string => {
        const token = localStorage.getItem('authToken');
        if (!token) {
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            navigate('/login');
            throw new Error('No authentication token found');
        }
        return token;
    }, [navigate]);

    const handleProfileUpdate = async (e: React.FormEvent, profileData: ProfileData) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setIsLoading(true);

        try {
            verifyToken();

            const response = await apiService.put<{ user: User }>(
                API_ENDPOINTS.PROFILE.UPDATE,
                profileData
            );

            // Update both localStorage and state if available
            if (response.user) {
                const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
                const updatedUser = {
                    ...currentUser,
                    fullName: response.user.fullName,
                    email: response.user.email
                };
                localStorage.setItem('user', JSON.stringify(updatedUser));
                if (setUser) {
                    setUser(updatedUser);
                }
            }

            setSuccess('Profile updated successfully');
            setTimeout(() => {
                setShowProfileModal(false);
                setSuccess(null);
            }, 2000);
        } catch (err) {
            if (err instanceof Error) {
                if (err.message.includes('401') || err.message.includes('403')) {
                    navigate('/login');
                    return;
                }
                setError(err.message);
            } else {
                setError('An error occurred');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handlePasswordChange = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setError("New passwords don't match");
            return;
        }

        if (passwordData.newPassword.length < 8) {
            setError("New password must be at least 8 characters long");
            return;
        }

        setIsLoading(true);

        try {
            verifyToken();

            await apiService.post(
                API_ENDPOINTS.AUTH.CHANGE_PASSWORD,
                {
                    currentPassword: passwordData.currentPassword,
                    newPassword: passwordData.newPassword
                }
            );

            setSuccess('Password changed successfully');
            setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });

            setTimeout(() => {
                setShowPasswordModal(false);
                setSuccess(null);
            }, 2000);
        } catch (err) {
            if (err instanceof Error) {
                if (err.message.includes('401') || err.message.includes('403')) {
                    navigate('/login');
                    return;
                }
                setError(err.message);
            } else {
                setError('An error occurred');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return {
        passwordData,
        setPasswordData,
        handlePasswordChange,
        handleProfileUpdate
    };
};